import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import DropdownMenu from '../../uikit/dropdown_menu';
import InfiniteScrollDropdown from '../../uikit/infinite_scroll_dropdown';
import { inventoryDetails } from '../../utils/apiUtils/inventoryUtils';
import useDropdowns from './dropdownUtil';

export const mapApi = (apiFn, extraParams = {}) => {
  return async ({ pageParam, pageSize, searchTerm }) => {
    return apiFn({
      pageNumber: pageParam,
      pageSize,
      searchString: searchTerm,
      ...extraParams,
    });
  };
};

const MaterialSelector = ({
  category = null,
  subCategory = null,
  material = null,
  onSelectMaterial,
  className = '',
  dropdownClassName = '',
  dropdownWrapperClassName = '',
  showLabel = true,
  labelClassName = '',
  errors = null,
  disableDropdownPortal = true,
  showAutoPo = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [selectedSubCategory, setSelectedSubCategory] = useState(subCategory);
  const [selectedMaterial, setSelectedMaterial] = useState(material);
  const [subCategories, setSubCategories] = useState([]);
  const [isLoadingSubCategories, setIsLoadingSubCategories] = useState(false);

  const {
    fetchCategories,
    fetchSubCategories,
    handleFetchError,
    transformCategoryOption,
    transformSubCategoryOption,
    transformMaterialOption,
  } = useDropdowns();

  // Fetch materials based on subcategory selection
  // If category is selected but subcategory is not, don't fetch materials
  // If no category is selected, fetch all materials
  const fetchMaterials = useMemo(() => {
    const extraParams = {};
    // Only add inventoryDetailsParentId if subcategory is selected
    if (selectedSubCategory?.value) {
      extraParams.inventoryDetailsParentId = selectedSubCategory.value;
    }
    return mapApi(inventoryDetails, extraParams);
  }, [selectedSubCategory]);

  // Load subcategories when category changes
  useEffect(() => {
    const loadSubCategories = async () => {
      if (selectedCategory?.value) {
        setIsLoadingSubCategories(true);
        try {
          const data = await fetchSubCategories(selectedCategory.value);
          const transformed = data.map(transformSubCategoryOption);
          setSubCategories(transformed);
        } catch (error) {
          handleFetchError('Failed to fetch subcategories');
          setSubCategories([]);
        } finally {
          setIsLoadingSubCategories(false);
        }
      } else {
        setSubCategories([]);
      }
    };
    loadSubCategories();
  }, [selectedCategory]);

  // Sync state when props change from parent.
  // Compare by `.value` to avoid triggering on every parent re-render
  // that produces a new object literal with the same value (e.g. when
  // an unrelated input like customVehicleName changes and the parent
  // re-renders, passing `{ value: X, label: Y }` as a fresh object).
  useEffect(() => {
    if (category?.value !== selectedCategory?.value) {
      setSelectedCategory(category);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.value]);

  useEffect(() => {
    if (subCategory?.value !== selectedSubCategory?.value) {
      setSelectedSubCategory(subCategory);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategory?.value]);

  useEffect(() => {
    if (material?.value !== selectedMaterial?.value) {
      setSelectedMaterial(material);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material?.value]);

  // Handle category selection (drill-down flow)
  const handleCategorySelect = (selected) => {
    setSelectedCategory(selected);
    // Clear subcategory and material when category changes
    setSelectedSubCategory(null);
    setSelectedMaterial(null);

    // Notify parent
    onSelectMaterial?.({
      category: selected,
      subCategory: null,
      material: null,
    });
  };

  // Handle subcategory selection (drill-down flow)
  const handleSubCategorySelect = (selected) => {
    setSelectedSubCategory(selected);
    // Clear material when subcategory changes
    setSelectedMaterial(null);

    // Notify parent
    onSelectMaterial?.({
      category: selectedCategory,
      subCategory: selected,
      material: null,
    });
  };

  // Handle material selection (material-first flow)
  const handleMaterialSelect = (selected) => {
    setSelectedMaterial(selected);

    let finalCategory = selectedCategory;
    let finalSubCategory = selectedSubCategory;

    // Auto-populate category and subcategory if material is selected
    // and they don't already match the material's category/subcategory
    if (selected?.category && selected?.subCategory) {
      // Update category if it's different
      if (selected.category.value !== selectedCategory?.value) {
        setSelectedCategory(selected.category);
        finalCategory = selected.category;
      }
      // Update subcategory if it's different
      if (selected.subCategory.value !== selectedSubCategory?.value) {
        setSelectedSubCategory(selected.subCategory);
        finalSubCategory = selected.subCategory;
      }
    }

    // Notify parent component with all three values
    onSelectMaterial?.({
      category: finalCategory,
      subCategory: finalSubCategory,
      material: selected,
    });
  };

  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        {/* CATEGORY */}
        <div className={`w-full ${dropdownWrapperClassName}`}>
          <InfiniteScrollDropdown
            required
            label={showLabel ? 'Category' : ''}
            labelClassName={labelClassName}
            placeholder="Category"
            className={`min-w-[220px] ${dropdownClassName}`}
            fetchFunction={fetchCategories}
            queryKey="categories-dropdown-menu"
            transformOption={transformCategoryOption}
            onFetchError={() => handleFetchError('Failed to fetch categories')}
            value={selectedCategory}
            onSelect={handleCategorySelect}
            disablePortal={disableDropdownPortal}
          />
          {errors && (
            <p className="mt-1 text-sm text-red-600">{errors.category}</p>
          )}
        </div>

        {/* SUB CATEGORY */}
        <div className={`w-full ${dropdownWrapperClassName}`}>
          <DropdownMenu
            required
            label={showLabel ? 'Sub Category' : ''}
            labelClassName={labelClassName}
            className={`min-w-[220px] ${dropdownClassName}`}
            placeholder="Sub Category"
            options={subCategories}
            value={selectedSubCategory}
            onSelect={handleSubCategorySelect}
            isLoading={isLoadingSubCategories}
            disabled={!selectedCategory}
            localSearch
            disablePortal={disableDropdownPortal}
          />
          {errors && (
            <p className="mt-1 text-sm text-red-600">{errors.subCategory}</p>
          )}
        </div>

        {/* MATERIAL */}
        <div className={`w-full ${dropdownWrapperClassName}`}>
          <InfiniteScrollDropdown
            triggerButtonClassName={`border rounded-lg ${
              errors ? 'border-red-500' : ''
            }`}
            required
            label={showLabel ? 'Material' : ''}
            labelClassName={labelClassName}
            placeholder="Material"
            className={`min-w-[220px] ${dropdownClassName}`}
            fetchFunction={fetchMaterials}
            queryKey={
              selectedSubCategory
                ? `materials-dropdown-${selectedSubCategory.value}`
                : 'materials-dropdown-all'
            }
            transformOption={(item) =>
              transformMaterialOption(item, showAutoPo)
            }
            onFetchError={() => handleFetchError('Failed to fetch materials')}
            value={selectedMaterial}
            onSelect={handleMaterialSelect}
            shouldFetch={selectedCategory ? !!selectedSubCategory : true}
            disabled={selectedCategory && !selectedSubCategory}
            disablePortal={disableDropdownPortal}
          />
          {errors && (
            <p className="mt-1 text-sm text-red-600">{errors.material}</p>
          )}
        </div>
      </div>
    </div>
  );
};

MaterialSelector.propTypes = {
  category: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  subCategory: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  material: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    measurementUnit: PropTypes.string,
    subCategory: PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
    category: PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  }),
  errors: PropTypes.shape({
    material: PropTypes.string,
    category: PropTypes.string,
    subCategory: PropTypes.string,
  }),
  onSelectMaterial: PropTypes.func,
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  dropdownWrapperClassName: PropTypes.string,
  showLabel: PropTypes.bool,
  labelClassName: PropTypes.string,
  disableDropdownPortal: PropTypes.bool,
  showAutoPo: PropTypes.bool,
};

export default MaterialSelector;
